import React, { useEffect, useState } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Paper, Chip, IconButton, Divider, Stack, Avatar, Tooltip, Dialog, DialogTitle, DialogContent } from '@mui/material';
import { ArrowBack, BusinessCenter, Apartment, Visibility, Close as CloseIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import GoalDetailsPage from './goalDetailsPage';

const MOCK_GOALS = [
    {
        id: 1,
        name: "Bharath M",
        goalTitle: "Frontend Optimization",
        target: "Reduce LCP by 20%",
        progress: 75,
        deadline: "2026-05-15",
        status: "In Progress",
        department: "Engineering",
        position: "Senior Frontend Developer",
        overallRating: 4.2
    }
];

function ViewPastGoals({ employee }) {
    const navigate = useNavigate();
    const [state, setState] = useState({
        searchQuery: '',
        filterStatus: 'All',
        selectedCardFilter: 'all',
        allGoals: [],
        totalCount: 0,
        stats: {},
        loading: false,
        tablePage: 0,
        rowsPerPage: 10,
        selectedGoal: null,
        isGoalDetailsOpen: false,
        snackbar: { open: false, message: '', severity: 'success' }
    });

    const updateState = (updates) => setState(prev => ({ ...prev, ...updates }));

    useEffect(() => {
        updateState({
            allGoals: MOCK_GOALS,
            totalCount: MOCK_GOALS.length
        });
    }, []);

    /**
     * KEY FIX: 
     * I renamed 'title' to 'goalTitle' to match what GoalDetailsPage expects.
     * I also added empty strings for properties like 'department' or 'position' 
     * just in case the child component tries to call .toLowerCase() on them.
     */
    const pastGoals = [
        {
            id: 1,
            name: employee?.name || "Bharath M",
            goalTitle: 'Q1 Sales Target', // Match MOCK_GOALS key
            target: 'Complete Swagger Docs',
            status: 'Completed',
            completionDate: '2023-03-31',
            achievement: '105%',
            department: employee?.department || '',
            position: employee?.designation || '',
            deadline: '2023-03-31',
            period: 'Q2 2025'
        },
        {
            id: 2,
            name: employee?.name || "Bharath M",
            goalTitle: 'AWS Certification',
            target: 'Complete Swagger Docs',
            status: 'Completed',
            completionDate: '2023-05-15',
            achievement: 'Pass',
            department: employee?.department || '',
            position: employee?.designation || '',
            deadline: '2023-05-15',
            period: 'Q1 2025'
        }
    ];

    return (
        <Box sx={{ p: 1 }}>
            <Stack direction="row" alignItems="center" sx={{ borderBottom: '1px solid #c3c3c3' }}>
                <IconButton
                    onClick={() => navigate('/manager/performance-management', { state: { activeTab: 0 } })}
                    sx={{ color: '#64748b', borderRadius: '8px' }}
                >
                    <ArrowBack sx={{ fontSize: 20 }} />
                    <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>Back</Typography>
                </IconButton>
                <Divider orientation="vertical" flexItem sx={{ mx: 3, height: 32 }} />
                <Box sx={{ mb: 1 }}>
                    <Typography variant="h6" fontWeight="bold">Goal History</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Viewing all archived goals for <b>{employee?.name || 'Bharath M'}</b>
                    </Typography>
                </Box>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            <Paper variant="outlined" sx={{ p: 2, mb: 4, bgcolor: '#f8fafc', borderStyle: 'dashed' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 4 }} divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: '#3b82f6', width: 40, height: 40 }}>
                            {employee?.name?.charAt(0) || 'E'}
                        </Avatar>
                        <Box>
                            <Typography variant="body1" fontWeight="bold">{employee?.name || 'Bharath M'}</Typography>
                            <Typography variant="caption" color="text.secondary">ID: {employee?.empId || 'EMP-001'}</Typography>
                        </Box>
                    </Stack>
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <Apartment sx={{ fontSize: 16, color: '#64748b' }} />
                            <Typography variant="subtitle2" color="text.secondary">Department</Typography>
                        </Stack>
                        <Typography variant="body2" fontWeight="500">{employee?.department || 'Engineering'}</Typography>
                    </Box>
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <BusinessCenter sx={{ fontSize: 16, color: '#64748b' }} />
                            <Typography variant="subtitle2" color="text.secondary">Designation</Typography>
                        </Stack>
                        <Typography variant="body2" fontWeight="500">{employee?.designation || 'Senior Developer'}</Typography>
                    </Box>
                </Stack>
            </Paper>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Archived Goals ({pastGoals.length})
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px' }}>
                <Table>
                    <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Goal Title</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Target</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Period</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Completion Date</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pastGoals.map((goal) => (
                            <TableRow key={goal.id} hover>
                                <TableCell>{goal.goalTitle}</TableCell>
                                <TableCell>{goal.target}</TableCell>
                                <TableCell>{goal.period}</TableCell>
                                <TableCell>{goal.completionDate}</TableCell>
                                <TableCell>
                                    <Chip label={goal.status} size="small" color={goal.status === 'Completed' ? 'success' : 'default'} />
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title="View Goal Details">
                                        <IconButton size="small" onClick={() => updateState({ selectedGoal: goal, isGoalDetailsOpen: true })}>
                                            <Visibility fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={state.totalCount}
                page={state.tablePage}
                rowsPerPage={state.rowsPerPage}
                onPageChange={(_, p) => updateState({ tablePage: p })}
                onRowsPerPageChange={(e) => updateState({ rowsPerPage: parseInt(e.target.value, 10), tablePage: 0 })}
            />

            <Dialog
                open={state.isGoalDetailsOpen}
                onClose={() => updateState({ isGoalDetailsOpen: false })}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8f9fa' }}>
                    <Box component="span" sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
                        Goal Details
                    </Box>
                    <Box>
                        <IconButton onClick={() => updateState({ isGoalDetailsOpen: false })}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {/* Render child only when selectedGoal is set */}
                    {state.selectedGoal ? (
                        <GoalDetailsPage goalData={state.selectedGoal} />
                    ) : (
                        <Typography>Loading...</Typography>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default ViewPastGoals;